import { Icon, styled, theme } from 'design-system';
import {
  isUploadErrorPayload,
  TUploadErrorCode,
  UploadButtonNudge,
  uploadErrorHeadings,
} from 'preflight-core';
import { useEffect, useState } from 'react';

import { Button } from '../../components/button';
import { ButtonGroup } from '../../components/button-group';
import { useEventTracking } from '../../contexts/tracking-context';
import { UploadState } from '../../utils/use-upload-document';

export const HeroErrorNudge = ({
  uploadState,
  onFileSelected,
  showSubmission = false,
  submissionUrl,
  align,
  uploadStateContinuePdf,
}: {
  uploadState: UploadState;
  onFileSelected: (file: File) => void;
  showSubmission?: boolean;
  submissionUrl?: string;
  align?: string;
  uploadStateContinuePdf?: {
    name: string;
    error?: { json: { errorCode: TUploadErrorCode; errorReason: string } };
    data?: { json: object };
  };
}) => {
  const { trackEvent } = useEventTracking();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [error, setError] = useState<any>('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (uploadStateContinuePdf?.name === 'error') {
      const errorCode =
        uploadStateContinuePdf?.error?.json?.errorCode || 'INVALID_RE_UPLOAD';
      setError(uploadErrorHeadings[errorCode]);
      setErrorMessage(uploadStateContinuePdf?.error?.json?.errorReason || '');
    } else if (
      uploadState.name === 'error' &&
      isUploadErrorPayload(uploadState.error?.json)
    ) {
      setError(uploadErrorHeadings[uploadState.error?.json?.errorCode]);
      setErrorMessage(uploadState.error?.json?.errorReason);
    }
  }, [uploadState.name, uploadStateContinuePdf?.name]);

  return (
    <>
      <SectionWrapper>
        {error ? (
          <>
            <Heading>{error}</Heading>
            <Para>
              {' '}
              <IconSpacing>
                <Icon height={16} variant={'regular'} name={'circleInfo'} />
              </IconSpacing>
              {errorMessage}
            </Para>
          </>
        ) : (
          <>
            <Heading>File uploading error</Heading>
            <Para>
              <IconSpacing>
                <Icon height={16} variant={'regular'} name={'circleInfo'} />{' '}
              </IconSpacing>{' '}
              We not quite sure what’s causing the issue. Please try again if
              the problem persists, reach out to us at{' '}
              <a href="mailto:preflight-support@paperpal.com">
                preflight-support@paperpal.com
              </a>
            </Para>
          </>
        )}
      </SectionWrapper>
      <ButtonsWrap
        style={{
          marginTop: '40px',
          justifyContent:
            align === 'center'
              ? 'center'
              : align === 'right'
              ? 'right'
              : 'left',
        }}
      >
        <ButtonGroup>
          {uploadState.name === 'error' ||
          uploadStateContinuePdf?.name === 'error' ? (
            <UploadButtonNudge
              id="Try-Uploading-Again"
              disabled={false}
              onClick={() => {
                trackEvent([
                  'Manuscript upload intent',
                  'Upload click',
                  'Try uploading file again',
                  {},
                ]);
              }}
              onFileSelected={onFileSelected}
              size="small"
              text="Try uploading file again"
            />
          ) : (
            <UploadButtonNudge
              id="Try-Uploading-Again"
              disabled={true}
              onClick={() => {}}
              onFileSelected={onFileSelected}
              size="small"
              text="Try uploading file again"
            />
          )}
          {showSubmission ? (
            submissionUrl ? (
              <Button
                id="Skip-to-submission"
                color="gray1"
                size="small"
                as="a"
                href={submissionUrl}
                onClick={() => {
                  trackEvent([
                    'Manuscript upload intent',
                    'Secondary click',
                    'Skip to submission',
                    {
                      Section: 'Section 1',
                    },
                  ]);
                }}
                style={{ fontSize: '20px' }}
              >
                <span>{'Skip to submission >'}</span>
              </Button>
            ) : (
              <div>
                <Button
                  id="Skip-to-submission"
                  disabled
                  size="small"
                  title="Contact the journal editor for submission URL"
                  style={{ fontSize: '20px' }}
                >
                  {'Skip to submission >'}
                </Button>
              </div>
            )
          ) : (
            <></>
          )}
        </ButtonGroup>
      </ButtonsWrap>
    </>
  );
};

const Heading = styled('div', {
  fontSize: '24px',
  fontWeight: '600',
  color: theme.colors.red10,
});

const Para = styled('div', {
  paddingLeft: '25px',
  marginLeft: '-25px',
  fontSize: '14px',
  color: theme.colors.red10,
  marginTop: '15px',
});

const SectionWrapper = styled('div', {
  border: `1px solid ${theme.colors.red10}`,
  padding: '20px',
  background: theme.colors.red3,
  borderRadius: '5px',
});

const IconSpacing = styled('span', {
  marginRight: '5px',
});

const ButtonsWrap = styled('div', {
  display: 'flex',
  'div > button': {
    height: '63px',
  },
  'div > button:hover': {
    backgroundColor: theme.colors.blue11,
  },
  'div > div > button:hover': {
    backgroundColor: theme.colors.white,
  },
  'div > a': {
    height: '63px',
    display: 'inline-flex',
    alignItems: 'center',
  },
});
